import { memo } from 'react';
import { Cancel as CancelIcon, Search as SearchIcon } from '@vgno/icons';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useRouteMatch } from 'react-router-dom';
import { Provider } from '@vgtv/api-client';

import { Logo } from '../Logo/Logo';
import { config } from '../../config';
import { ViewportType } from '../../utils/viewport/getViewportType';
import { selectIsHeaderSticky } from '../../modules/ui';
import { Button } from '../Button/Button';
import { MQ_MOBILE_NAV, useMediaQuery } from '../utils/useMediaQuery';
import { useOverlay } from '../Overlay/useOverlay';
import { SearchBox } from '../SearchBox/SearchBox';
import { getRoute, routeNames } from '../../shared/helpers/routes';
import {
  hasValidLoginAccess,
  hasValidSubscriptionAccess,
} from '../../shared/helpers/subscriptionHelpers';
import { useAccessContext } from '../AccessProvider/AccessProvider';
import { isAndroid, isHermesApp } from '../../utils/device';
import { useViewportType } from '../utils/useViewportType';

import { getMenuItems } from './helpers';
import { DesktopNavigation } from './DesktopNavigation/DesktopNavigation';
import { UserMenu } from './UserMenu/UserMenu';
import { UserAvatar } from './UserMenu/UserAvatar/UserAvatar';
import { SearchButton } from './SearchButton/SearchButton';
import { SubscribeButton } from './SubscribeButton/SubscribeButton';
import { LoginButton } from './LoginButton/LoginButton';

import type { FC } from 'react';

import * as styles from './Header.module.scss';

const desktopMenuItems = getMenuItems(config, ViewportType.DESKTOP);

type Props = {
  onSearchClick?: () => void;
};

const HeaderBase: FC<Props> = ({ onSearchClick }) => {
  const isStickyHeader = useSelector(selectIsHeaderSticky);
  const { isOpen, toggle, close, ariaAttributes } = useOverlay();
  const { accesses, isFetched } = useAccessContext();
  const { isDesktop } = useViewportType();
  const isMobile = useMediaQuery(MQ_MOBILE_NAV);

  const isSearchPage = Boolean(useRouteMatch(getRoute(routeNames.search)));
  const displaySearchBox =
    config.provider === Provider.VGTV && isSearchPage && isMobile;

  const getActionButton = () => {
    if (isFetched && !displaySearchBox) {
      if (!hasValidLoginAccess(accesses)) {
        return <LoginButton />;
      }

      if (!hasValidSubscriptionAccess(accesses)) {
        let label: string | undefined;
        if (config.provider === Provider.VGTV) {
          label = isDesktop ? 'Sommertilbud på VG+' : 'Sommertilbud';
        }
        return isHermesApp && isAndroid ? null : (
          <SubscribeButton label={label} />
        );
      }
    }

    return null;
  };

  return (
    <>
      {isOpen && <div className={styles.spacer}></div>}
      <header
        className={classNames(styles.header, {
          [styles.sticky]: isStickyHeader || isOpen,
        })}
      >
        {displaySearchBox ? (
          <div className={styles.searchBox}>
            <SearchBox variant="mobile" />
          </div>
        ) : (
          <div className={styles.logo}>
            <Logo />
          </div>
        )}
        {!isMobile && (
          <DesktopNavigation
            className={styles.desktopNavigation}
            mainMenuItems={desktopMenuItems.mainMenuItems}
            subMenuItems={desktopMenuItems.subMenuItems}
          />
        )}
        <aside className={styles.aside}>
          {getActionButton()}
          <Button
            variant="text"
            {...ariaAttributes}
            className={classNames(styles.button, { [styles.avatar]: !isOpen })}
            onClick={toggle}
          >
            {isOpen ? <CancelIcon /> : <UserAvatar />}
          </Button>
          {config.provider === Provider.VGTV ? (
            <SearchButton active={isSearchPage} className={styles.button} />
          ) : (
            <Button
              variant="text"
              onClick={onSearchClick}
              className={styles.button}
            >
              <SearchIcon />
            </Button>
          )}
        </aside>
        <UserMenu isOpen={isOpen} dismiss={close} />
      </header>
    </>
  );
};

export const Header = memo(HeaderBase);
